import { Menu } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import MoreHorizOutlinedIcon from '@material-ui/icons/MoreHorizOutlined';
import ScannerIcon from '@material-ui/icons/Scanner';
import React from 'react';
import { SmallPrimaryTextButton } from 'framework/components/buttons/SmallPrimaryTextButton';
import { DrawIcon } from 'framework/components/icons/DrawIcon';
import { FileDocumentEditOutlineIcon } from 'framework/components/icons/FileDocumentEditOutlineIcon';
import { FileDownloadOutlineIcon } from 'framework/components/icons/FileDownloadOutlineIcon';
import { FileSearchOutlineIcon } from 'framework/components/icons/FileSearchOutlineIcon';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { useAnchorElement } from 'framework/hooks/useAnchorElement';
import { DocumentExportFormat, documentsCommand_delete, documentsQuery_download, IDocument } from 'gen/ApiClient';
import { useDownload } from 'gen/useDownload';
import { useLocalization } from 'localization/useLocalization';
import { useReplaceDocument } from 'shared/dialogs/scanOrUpload/useReplaceDocument';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { useDocumentEditor } from 'shared/dialogs/useDocumentEditor';
import { usePdfViewerWithDocument } from 'shared/dialogs/usePdfViewerWithDocument';

const canPlaceDigitalSignature = false;

interface IProps {
	document: IDocument;
	reload: VoidFunction;
	prependActions?: JSX.Element;
	appendActions?: JSX.Element;
	close: VoidFunction;
}

export const DocumentActions = ({ document, reload, close, prependActions, appendActions }: IProps) => {
	const [download] = useDownload(documentsQuery_download);
	const [openEditor] = useDocumentEditor(reload);
	const openPdf = usePdfViewerWithDocument();
	const replace = useReplaceDocument(reload);
	const { open, cancel, confirm } = useDialogsContext(reload);
	const [anchor, openMenu, closeMenu] = useAnchorElement();
	const strings = useLocalization();

	if (document === undefined) {
		return <div></div>;
	}

	const onViewPdf = () => {
		openPdf(document.typeOrName ?? '', document.id);
	};

	const onViewOrEdit = async () => {
		openEditor(document.id);
	};

	const onDelete = () => {
		open(
			<AlertDialog
				open
				content={strings.deleteWhatQuestion(strings.document)}
				acceptText={strings.yesWhatExclamation(strings.delete)}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => documentsCommand_delete(document.id)}
				accept={() => {
					confirm();
					close();
				}}
			/>
		);
	};

	const onDownload = (format: DocumentExportFormat) => {
		download(document.id, format);
	};

	return (
		<>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={closeMenu}
				keepMounted>
				<div
					className='df-col'
					style={{
						alignItems: 'flex-start',
						paddingTop: 8,
						paddingBottom: 8,
						paddingLeft: 16,
						paddingRight: 16,
					}}>
					{document.isPdf === false && document.canConvertToPdf && (
						<SmallPrimaryTextButton
							onClick={onViewPdf}
							startIcon={<FileSearchOutlineIcon />}>
							{`${strings.viewDocument} (.pdf, ${strings.inBrowser})`}
						</SmallPrimaryTextButton>
					)}
					<SmallPrimaryTextButton
						onClick={() => onDownload('Native')}
						startIcon={<FileDownloadOutlineIcon />}>
						{`${strings.downloadDocument} (${document.extension})`}
					</SmallPrimaryTextButton>
					{document.isPdf === false && document.canConvertToPdf && (
						<SmallPrimaryTextButton
							onClick={() => onDownload('Pdf')}
							startIcon={<FileDownloadOutlineIcon />}>
							{`${strings.downloadDocument} (.pdf)`}
						</SmallPrimaryTextButton>
					)}
					<SmallPrimaryTextButton
						onClick={() => replace(document)}
						startIcon={<ScannerIcon />}>
						{`${strings.uploadReplaceDocument}`}
					</SmallPrimaryTextButton>
					{canPlaceDigitalSignature && document.mustSign && document.hasSignature === false && (
						<SmallPrimaryTextButton startIcon={<DrawIcon />}>{strings.placeDigitalSignature}</SmallPrimaryTextButton>
					)}
					{appendActions && appendActions}
					<SmallPrimaryTextButton
						color='secondary'
						startIcon={<DeleteOutlineIcon />}
						onClick={onDelete}>
						{strings.deleteWhat(strings.document)}
					</SmallPrimaryTextButton>
				</div>
			</Menu>
			<div className='df-col-as'>
				{prependActions && prependActions}
				<DocumentPrimaryAction
					document={document}
					onViewOrEdit={onViewOrEdit}
					onDownload={onDownload}
					onViewPdf={onViewPdf}
				/>
				<SmallPrimaryTextButton
					startIcon={<MoreHorizOutlinedIcon />}
					onClick={openMenu}>
					{strings.moreActions}
				</SmallPrimaryTextButton>
			</div>
		</>
	);
};

interface IDocumentPrimaryActionProps {
	document: IDocument;
	onViewPdf: VoidFunction;
	onViewOrEdit: VoidFunction;
	onDownload: (format: DocumentExportFormat) => void;
}

const DocumentPrimaryAction = ({ document, onViewPdf, onViewOrEdit, onDownload }: IDocumentPrimaryActionProps) => {
	const strings = useLocalization();

	if (document.isPdf) {
		return (
			<SmallPrimaryTextButton
				onClick={onViewPdf}
				startIcon={<FileSearchOutlineIcon />}>
				{strings.viewDocument}
			</SmallPrimaryTextButton>
		);
	} else if (document.isDocx && document.canEdit && document.canViewOnline) {
		return (
			<SmallPrimaryTextButton
				onClick={onViewOrEdit}
				startIcon={<FileDocumentEditOutlineIcon />}>
				{strings.editWhat(strings.document)}
			</SmallPrimaryTextButton>
		);
	} else {
		return (
			<SmallPrimaryTextButton
				onClick={() => onDownload('Native')}
				startIcon={<FileDownloadOutlineIcon />}>
				{`${strings.downloadDocument} (${document.extension})`}
			</SmallPrimaryTextButton>
		);
	}
};
